import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from 'src/context/ToastProvider';
import { useMutation, useQueryClient } from 'react-query';
import { Button, FileInput, Modal, Spinner } from 'flowbite-react';
import { getPolicyForS3NonVideo, updateUserById } from 'src/apis/user';
import { useUserContext } from 'src/context/UserProvider';
import { handleS3Upload } from 'src/utils/imageUpload';
import { get } from 'lodash';
import Webcam from 'react-webcam';

type Props = {
	isOpen: boolean;
	onClose: () => void;
};

async function dataURLToFile(dataUrl: string, fileName: string): Promise<File> {
	const response = await fetch(dataUrl);
	const blob = await response.blob();
	return new File([blob], fileName, { type: blob.type });
}

function fileToDataURL(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = error => reject(error);
		reader.readAsDataURL(file);
	});
}

const CaptureAndUploadProfileImage = ({ isOpen, onClose }: Props) => {
	const [file, setFile] = useState<File | null>();
	const toast = useToast();
	const { user } = useUserContext();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const queryClient = useQueryClient();

	const [img, setImg] = useState<string | null>(null);
	const webcamRef = useRef<Webcam | null>(null);

	const videoConstraints = {
		width: 420,
		height: 420,
		facingMode: 'user',
	};

	const capture = useCallback(async () => {
		if (webcamRef?.current) {
			const imageSrc = webcamRef?.current?.getScreenshot();

			setImg(imageSrc);

			if (imageSrc) {
				const file = await dataURLToFile(imageSrc, 'proctoringImage.png');
				console.log('file:', file);
				setFile(file);
			}
		}
	}, [webcamRef?.current]);

	const hiddenFileInput = useRef<HTMLInputElement | null>(null);

	const updateProfileByIdFn = useMutation(['updateUserById'], updateUserById, {
		onSuccess() {
			queryClient.refetchQueries('getUserProfile');
			queryClient.refetchQueries('verifyCookie');
			toast({
				title: 'Profile image updated successfully',
				type: 'success',
				duration: 3000,
			});
			setIsLoading(false);
			onClose();
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			setIsLoading(false);
			onClose();
		},
	});

	const handleSubmitUser = async () => {
		if (file) {
			const fileSizeMB = file.size / (1024 * 1024); // Convert bytes to MB
			if (fileSizeMB > 5) {
				toast({
					type: 'error',
					title: 'File size exceeds the maximum limit of 5 MB',
					duration: 10000,
				});
				return;
			}
			setIsLoading(true);
			let imageUrl = '';
			if (file && user) {
				// fileName, fileLocation, mime
				const policy = await getPolicyForS3NonVideo(file.name, 'profileImages', file.type, true);
				const uploadedUrl = await handleS3Upload(policy, file);
				if (uploadedUrl) imageUrl = uploadedUrl;
				if (imageUrl) {
					updateProfileByIdFn.mutate({
						id: String(user?._id),
						data: {
							profile: {
								proctoringImageUrl: imageUrl,
							},
						},
					});
				}
			} else {
				setIsLoading(false);
			}
		}
	};

	return (
		<Modal dismissible={true} show={isOpen} onClose={onClose} size="lg" className="!h-full backdrop-blur-[4px]">
			<Modal.Header className="rounded-t-md dark:bg-[#1F2A37]">
				<div>
					<p className="font-inter text-[20px] font-[700] dark:text-white">Profile Image</p>
				</div>
			</Modal.Header>
			<Modal.Body className="dark:bg-[#1F2A37] p-0">
				<div>
					{img === null ? (
						<>
							<div className="min-h-fit md:min-h-[410px]">
								<Webcam
									audio={false}
									mirrored={true}
									height={'100%'}
									width={'100%'}
									ref={webcamRef}
									screenshotFormat="image/jpeg"
									videoConstraints={videoConstraints}
								/>
							</div>
						</>
					) : (
						<>
							<img
								src={img}
								alt="screenshot"
								width="100%"
								height="100%"
								className="w-full h-full object-cover object-center mx-auto min-h-[400px]"
							/>
						</>
					)}
				</div>
			</Modal.Body>
			<Modal.Footer className="rounded-b-md py-3 bg-gray-100 dark:bg-[#18222D]">
				<div className="flex w-full md:justify-end gap-3 items-center flex-wrap">
					{!img ? (
						<>
							<Button
								size="0"
								color="light"
								className="w-full max-w-[144px] [&_*]:w-full cursor-pointer p-0 focus-visible:outline-blue-300 "
							>
								<label htmlFor="file-input" className="cursor-[inherit] py-2 px-4">
									<input
										hidden
										type="file"
										accept="image/jpg"
										id="file-input"
										ref={hiddenFileInput}
										onChange={async e => {
											if (e?.target?.files?.length) {
												setFile(e?.target?.files[0]);
												const image = await fileToDataURL(e?.target?.files[0]);
												setImg(image);
											}
										}}
									/>
									Upload
								</label>
							</Button>

							<Button color="purplePlaced" className="w-full max-w-[144px]" onClick={capture}>
								Capture Photo
							</Button>
						</>
					) : (
						<>
							<Button
								color="light"
								className="w-full max-w-[144px]"
								onClick={() => {
									setImg(null);
									setFile(null);
								}}
							>
								Retake
							</Button>
							<Button
								color="purplePlaced"
								className="bg-[#7777E7] w-full max-w-[144px]"
								disabled={isLoading}
								onClick={handleSubmitUser}
							>
								{isLoading && <Spinner className="mr-3" size={'sm'} light={true} />}
								Save
							</Button>
						</>
					)}
				</div>
			</Modal.Footer>
		</Modal>
	);
};

export default CaptureAndUploadProfileImage;
